import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router";
import type { JsonValue } from "@protobuf-ts/runtime";
import { useFamilyDefaults } from "@/api/queries";
import { Choice, MoneyField, NumberField } from "@/components/fields";
import { copyText } from "@/copy/copy";
import type { Family } from "@/generated/core/v1/family";
import type { FieldEdit } from "@/generated/core/v1/questionnaire";
import { Value } from "@/generated/google/protobuf/struct";
import { money } from "@/plan-logic/lib/format";
import { newUuid } from "@/plan-logic/lib/uuid";
import {
  DefaultDecision_Decision,
  editsQuestionnaire,
  fieldEdit,
  paths,
} from "@/plan-logic/questionnaire/build";
import {
  familyJson,
  pathLabel,
  pathPerson,
  valueAt,
  valueText,
} from "@/plan-logic/questionnaire/paths";
import { yearsInCanadaIssue } from "@/questionnaire/yearsInCanada";
import { DataChecks } from "../dataChecks";
import { PlanPage, type PlanCtx, usePlanCtx } from "../PlanPage";
import { SaveFeedback, useFamilySave } from "../useFamilySave";
import {
  ACC_FLOOR,
  accuracyNote,
  planAccuracy,
  SHARPEN_WEIGHTS,
  type SharpenId,
  sharpenApplies,
  sharpenDone,
} from "./accuracy";

type ItemProps = {
  family: Family;
  onSave: (edits: FieldEdit[]) => Promise<boolean>;
  saving: boolean;
};

const name = (family: Family, i: number) =>
  family.persons[i]?.givenName || (i ? "Your partner" : "You");

function SaveRow({
  disabled,
  saving,
  onClick,
}: {
  disabled: boolean;
  saving: boolean;
  onClick: () => void;
}) {
  return (
    <div style={{ marginTop: 14 }}>
      <button
        type="button"
        className="btn"
        disabled={disabled || saving}
        onClick={onClick}
        data-testid="sharpen-save"
      >
        {saving ? "Saving…" : "Save and recalculate"}
      </button>
    </div>
  );
}

/** Occupation and the year each job started (persons[].jobs[].occupation / start_date). */
function JobItem({ family, onSave, saving }: ItemProps) {
  const jobs = family.persons.flatMap((p, pi) => p.jobs.map((j) => ({ p, pi, j })));
  const [v, setV] = useState<Record<string, { occupation: string; year?: number }>>(() =>
    Object.fromEntries(
      jobs.map(({ j }) => [
        j.uuid,
        { occupation: j.occupation, year: j.startDate?.year || undefined },
      ]),
    ),
  );
  const edits = jobs.flatMap(({ p, j }) => {
    const x = v[j.uuid];
    const out: FieldEdit[] = [];
    if (x.occupation.trim() && x.occupation.trim() !== j.occupation)
      out.push(fieldEdit(paths.job(p.uuid, j.uuid, "occupation"), x.occupation.trim()));
    if (x.year && x.year !== j.startDate?.year)
      out.push(
        fieldEdit(paths.job(p.uuid, j.uuid, "start_date"), { year: x.year, month: 0, day: 0 }),
      );
    return out;
  });
  return (
    <div className="walk">
      <h4>Where to find it</h4>
      <ol className="steps">
        <li>
          Your <b>most recent pension statement</b> shows credited service and the date it started.
          That is the number we want.
        </li>
        <li>
          No pension statement? Your <b>offer letter</b> or your first pay stub with this employer
          works.
        </li>
        <li>
          If you changed roles with the same employer, use the date you joined the <b>employer</b>,
          not the date you changed roles.
        </li>
      </ol>
      <div className="res">
        {jobs.map(({ pi, j }, k) => (
          <div className="two" key={j.uuid} style={{ marginTop: k ? 12 : 0 }}>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`occ-${j.uuid}`}>
                {name(family, pi)} &mdash; occupation
                {jobs.filter((x) => x.pi === pi).length > 1
                  ? ` (${j.title || j.employer || `job ${k + 1}`})`
                  : ""}
              </label>
              <input
                id={`occ-${j.uuid}`}
                type="text"
                placeholder="e.g. Registered nurse"
                value={v[j.uuid].occupation}
                onChange={(e) =>
                  setV({ ...v, [j.uuid]: { ...v[j.uuid], occupation: e.target.value } })
                }
              />
            </div>
            <NumberField
              label={`${name(family, pi)} — year you started`}
              value={v[j.uuid].year}
              onChange={(year) => setV({ ...v, [j.uuid]: { ...v[j.uuid], year } })}
              placeholder="e.g. 2011"
              style={{ margin: 0 }}
            />
          </div>
        ))}
        <p className="hint" style={{ marginTop: 12 }}>
          Change either of these later under <b>What you earn</b> on the Update Data page.
        </p>
        <SaveRow disabled={!edits.length} saving={saving} onClick={() => void onSave(edits)} />
      </div>
    </div>
  );
}

/** TFSA, RRSP and FHSA room (persons[].registered_room.*, is_first_time_home_buyer). */
function RoomItem({ family, onSave, saving }: ItemProps) {
  const year = new Date().getFullYear();
  const [v, setV] = useState<
    Record<
      string,
      {
        tfsa?: number;
        tfsaIn?: number;
        rrsp?: number;
        rrspUnused?: number;
        owned?: boolean;
        fhsa?: number;
      }
    >
  >(() =>
    Object.fromEntries(
      family.persons.map((p) => [
        p.uuid,
        {
          tfsa: p.registeredRoom?.tfsaRoom,
          rrsp: p.registeredRoom?.rrspDeductionLimit,
          rrspUnused: p.registeredRoom?.rrspUnusedContributions,
          fhsa: p.registeredRoom?.fhsaParticipationRoom,
          // A first-time home buyer (onboarding's or Update Data's box) hasn't owned a home.
          owned:
            p.registeredRoom?.fhsaParticipationRoom !== undefined || p.isFirstTimeHomeBuyer
              ? false
              : undefined,
        },
      ]),
    ),
  );
  const set = (uuid: string, patch: Partial<(typeof v)[string]>) =>
    setV({ ...v, [uuid]: { ...v[uuid], ...patch } });
  const edits: FieldEdit[] = family.persons.flatMap((p) => {
    const x = v[p.uuid];
    const out: FieldEdit[] = [];
    if (x.tfsa !== undefined)
      out.push(fieldEdit(paths.room(p.uuid, "tfsa_room"), Math.max(0, x.tfsa - (x.tfsaIn ?? 0))));
    if (x.rrsp !== undefined)
      out.push(fieldEdit(paths.room(p.uuid, "rrsp_deduction_limit"), x.rrsp));
    if (x.rrspUnused !== undefined)
      out.push(fieldEdit(paths.room(p.uuid, "rrsp_unused_contributions"), x.rrspUnused));
    if (x.owned === true)
      out.push(fieldEdit(paths.personField(p.uuid, "is_first_time_home_buyer"), false));
    if (x.owned === false) {
      out.push(fieldEdit(paths.personField(p.uuid, "is_first_time_home_buyer"), true));
      if (x.fhsa !== undefined)
        out.push(fieldEdit(paths.room(p.uuid, "fhsa_participation_room"), x.fhsa));
    }
    return out;
  });
  return (
    <>
      {family.persons.map((p, pi) => {
        const x = v[p.uuid];
        const who = family.persons.length > 1 ? `${name(family, pi)} — ` : "";
        return (
          <div key={p.uuid} style={{ marginTop: pi ? 22 : 0 }}>
            <div className="walk">
              <h4>{who}TFSA room</h4>
              <div className="trap">
                <b>The trap:</b> the CRA figure is only correct as of 1 January. Anything
                you&rsquo;ve put in since then isn&rsquo;t reflected, and anything you took out this
                year doesn&rsquo;t free up room until next January.
              </div>
              <ol className="steps">
                <li>
                  Sign in to <b>CRA My Account</b> and open{" "}
                  <b>Accounts and payments &rarr; Tax-Free Savings Account</b>.
                </li>
                <li>
                  Write down <b>&ldquo;TFSA contribution room as of January 1, {year}.&rdquo;</b>
                </li>
                <li>
                  Put what you&rsquo;ve contributed since 1 January {year}, across every TFSA, in
                  the second box. <b>Don&rsquo;t add back</b> anything you withdrew this year.
                </li>
              </ol>
              <div className="res">
                <div className="two">
                  <MoneyField
                    label={`CRA room as of 1 Jan ${year}`}
                    value={x.tfsa}
                    onChange={(tfsa) => set(p.uuid, { tfsa })}
                    placeholder="0"
                    style={{ margin: 0 }}
                    testId={`room-tfsa-${pi}`}
                  />
                  <MoneyField
                    label={`Contributed since 1 Jan ${year}`}
                    value={x.tfsaIn}
                    onChange={(tfsaIn) => set(p.uuid, { tfsaIn })}
                    placeholder="0"
                    style={{ margin: 0 }}
                  />
                </div>
                <div style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
                  Room available today:{" "}
                  <b style={{ color: "var(--ink)" }}>
                    {x.tfsa === undefined
                      ? "enter both to see it"
                      : money(Math.max(0, x.tfsa - (x.tfsaIn ?? 0)))}
                  </b>
                </div>
              </div>
            </div>
            <div className="walk" style={{ marginTop: 14 }}>
              <h4>{who}RRSP room</h4>
              <div className="trap">
                <b>The trap:</b> contributions made in the first 60 days of this year may already
                have been claimed on last year&rsquo;s return. If so, don&rsquo;t count them twice.
              </div>
              <ol className="steps">
                <li>
                  In <b>CRA My Account</b>, open <b>RRSP and TFSA &rarr; RRSP</b>, or look at your
                  latest <b>Notice of Assessment</b>.
                </li>
                <li>
                  Write down your <b>&ldquo;RRSP deduction limit for {year}.&rdquo;</b>
                </li>
                <li>
                  If you have a workplace pension, your limit is already reduced by your pension
                  adjustment. Don&rsquo;t subtract it again.
                </li>
              </ol>
              <div className="res">
                <div className="two">
                  <MoneyField
                    label={`RRSP deduction limit for ${year}`}
                    value={x.rrsp}
                    onChange={(rrsp) => set(p.uuid, { rrsp })}
                    placeholder="0"
                    style={{ margin: 0 }}
                  />
                  <MoneyField
                    label="Contributed and not yet deducted"
                    value={x.rrspUnused}
                    onChange={(rrspUnused) => set(p.uuid, { rrspUnused })}
                    placeholder="0"
                    style={{ margin: 0 }}
                  />
                </div>
              </div>
            </div>
            <div className="walk" style={{ marginTop: 14 }}>
              <h4>{who}FHSA room</h4>
              <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: "2px 0 0" }}>
                The First Home Savings Account is the only account that is deductible going in{" "}
                <i>and</i> tax-free coming out, but not everyone can open one.
              </p>
              <div className="choices" style={{ marginTop: 14 }}>
                <Choice
                  selected={x.owned === true}
                  onClick={() => set(p.uuid, { owned: true })}
                  icon="🏠"
                  title="I have owned a home in Canada"
                >
                  If you lived in a home you or your partner owned this year or in any of the four
                  calendar years before it, you can&rsquo;t open an FHSA.
                </Choice>
                <Choice
                  selected={x.owned === false}
                  onClick={() => set(p.uuid, { owned: false })}
                  icon="✓"
                  title="I haven't"
                >
                  Then you qualify, and this is usually the first account we&rsquo;d fill.
                </Choice>
              </div>
              {x.owned === false && (
                <div className="res" style={{ marginTop: 12 }}>
                  <MoneyField
                    label="FHSA room available today"
                    value={x.fhsa}
                    onChange={(fhsa) => set(p.uuid, { fhsa })}
                    placeholder="0"
                    style={{ margin: 0, maxWidth: 240 }}
                    hint="Never opened one? Your room is $0 until you do."
                  />
                </div>
              )}
              {x.owned === true && (
                <p className="hint" style={{ marginTop: 12 }}>
                  Understood. We&rsquo;ll leave the FHSA out of your plan.
                </p>
              )}
            </div>
          </div>
        );
      })}
      <SaveRow disabled={!edits.length} saving={saving} onClick={() => void onSave(edits)} />
    </>
  );
}

type WorkKind = "db" | "dc" | "both" | "none";

/** What the household has for retirement through work (persons[].pensions[]). */
function WorkItem({ family, onSave, saving }: ItemProps) {
  const pensions = family.persons.flatMap((p, pi) => p.pensions.map((pn) => ({ p, pi, pn })));
  const [v, setV] = useState<
    Record<
      string,
      {
        kind?: WorkKind;
        accrual?: number;
        service?: number;
        age?: number;
        balance?: number;
        you?: number;
        match?: number;
      }
    >
  >({});
  const set = (u: string, patch: Partial<(typeof v)[string]>) =>
    setV({ ...v, [u]: { ...v[u], ...patch } });
  const edits: FieldEdit[] = pensions.flatMap(({ p, pn }) => {
    const x = v[pn.uuid];
    if (!x?.kind) return [];
    const base = `persons[${p.uuid}].pensions[${pn.uuid}]`;
    const db: JsonValue = {
      ...(x.accrual !== undefined ? { below_ympe_accrual_rate: x.accrual } : {}),
      ...(x.service !== undefined ? { credited_service_years: x.service } : {}),
      ...(x.age !== undefined ? { earliest_unreduced_age: x.age } : {}),
    };
    const dc: JsonValue = {
      ...(x.balance !== undefined ? { current_balance: x.balance } : {}),
      ...(x.you !== undefined ? { employee_contribution_rate: x.you } : {}),
      ...(x.match !== undefined ? { matched_employer_contribution_rate: x.match } : {}),
    };
    if (x.kind === "none") return [fieldEdit(`${base}.pension_type`, "PENSION_TYPE_NO_PENSION")];
    if (x.kind === "db")
      return [
        fieldEdit(`${base}.pension_type`, "PENSION_TYPE_DEFINED_BENEFIT"),
        fieldEdit(`${base}.db_details`, db),
      ];
    if (x.kind === "dc")
      return [
        fieldEdit(`${base}.pension_type`, "PENSION_TYPE_DEFINED_CONTRIBUTION"),
        fieldEdit(`${base}.dc_details`, dc),
      ];
    const extra = newUuid();
    return [
      fieldEdit(`${base}.pension_type`, "PENSION_TYPE_DEFINED_BENEFIT"),
      fieldEdit(`${base}.db_details`, db),
      fieldEdit(`persons[${p.uuid}].pensions[${extra}]`, {
        uuid: extra,
        name: "Workplace savings plan",
        pension_type: "PENSION_TYPE_DEFINED_CONTRIBUTION",
        dc_details: dc,
      }),
    ];
  });
  return (
    <>
      {pensions.map(({ pi, pn }) => {
        const x = v[pn.uuid] ?? {};
        const showDb = x.kind === "db" || x.kind === "both";
        const showDc = x.kind === "dc" || x.kind === "both";
        return (
          <div key={pn.uuid}>
            <div className="walk">
              <h4>
                {family.persons.length > 1 ? `${name(family, pi)}: ` : ""}First, which one is it?
              </h4>
              <p style={{ fontSize: 13.5, color: "var(--ink-2)", margin: "2px 0 0" }}>
                Your pay stub is the fastest way to tell. Look at what comes off each cheque, and
                what it&rsquo;s called.
              </p>
              <div className="choices" style={{ marginTop: 14 }}>
                <Choice
                  selected={x.kind === "db"}
                  onClick={() => set(pn.uuid, { kind: "db" })}
                  icon="🏦"
                  title="A pension the company promises to pay me"
                >
                  A set monthly income for life once you retire, usually based on your salary and
                  years of service. Often called a <b>defined benefit</b> plan.
                </Choice>
                <Choice
                  selected={x.kind === "dc"}
                  onClick={() => set(pn.uuid, { kind: "dc" })}
                  icon="💸"
                  title="An account with money in it that grows"
                >
                  You put in, the employer often matches, and the balance is yours. Called a{" "}
                  <b>group RRSP</b>, <b>group savings plan</b>, <b>DPSP</b>, or{" "}
                  <b>defined contribution</b> plan.
                </Choice>
                <Choice
                  selected={x.kind === "both"}
                  onClick={() => set(pn.uuid, { kind: "both" })}
                  icon="🔄"
                  title="Both"
                >
                  If two separate retirement lines come off your pay, this is you.
                </Choice>
                <Choice
                  selected={x.kind === "none"}
                  onClick={() => set(pn.uuid, { kind: "none" })}
                  icon="⚪"
                  title="Neither, I was wrong"
                >
                  Nothing comes off your pay for retirement. We&rsquo;ll take it out of your plan.
                </Choice>
              </div>
            </div>
            {showDb && (
              <div className="walk" style={{ marginTop: 14 }}>
                <h4>Your defined benefit pension</h4>
                <div className="trap">
                  <b>Why this matters:</b> a promised income for life is worth a large amount of
                  capital you&rsquo;d otherwise have to save yourself.
                </div>
                <ol className="steps">
                  <li>
                    Find your most recent <b>annual pension statement</b>.
                  </li>
                  <li>
                    Look for the <b>accrual rate</b> &mdash; a small percentage like 1.5% or 2%.
                  </li>
                  <li>
                    Find your <b>years of credited service</b> so far, and the <b>earliest age</b>{" "}
                    you can start collecting without a reduction.
                  </li>
                </ol>
                <div className="res three">
                  <NumberField
                    label="Accrual rate (%)"
                    value={x.accrual}
                    onChange={(accrual) => set(pn.uuid, { accrual })}
                    placeholder="e.g. 2"
                    style={{ margin: 0 }}
                  />
                  <NumberField
                    label="Years of credited service"
                    value={x.service}
                    onChange={(service) => set(pn.uuid, { service })}
                    placeholder="e.g. 12"
                    style={{ margin: 0 }}
                  />
                  <NumberField
                    label="Earliest unreduced age"
                    value={x.age}
                    onChange={(age) => set(pn.uuid, { age })}
                    placeholder="e.g. 60"
                    style={{ margin: 0 }}
                  />
                </div>
              </div>
            )}
            {showDc && (
              <div className="walk" style={{ marginTop: 14 }}>
                <h4>Your group RRSP, group savings plan or DPSP</h4>
                <div className="trap">
                  <b>Why this matters:</b> the employer match is the highest guaranteed return
                  you&rsquo;ll ever get.
                </div>
                <div className="res three">
                  <MoneyField
                    label="Current balance"
                    value={x.balance}
                    onChange={(balance) => set(pn.uuid, { balance })}
                    style={{ margin: 0 }}
                  />
                  <NumberField
                    label="You put in (% of pay)"
                    value={x.you}
                    onChange={(you) => set(pn.uuid, { you })}
                    placeholder="e.g. 5"
                    style={{ margin: 0 }}
                  />
                  <NumberField
                    label="Employer matches (% of pay)"
                    value={x.match}
                    onChange={(match) => set(pn.uuid, { match })}
                    placeholder="e.g. 3"
                    style={{ margin: 0 }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <SaveRow disabled={!edits.length} saving={saving} onClick={() => void onSave(edits)} />
    </>
  );
}

/** Years in Canada since 18 (persons[].oas_residency.years_in_canada_after_18). */
function OasItem({ family, onSave, saving }: ItemProps) {
  const { today } = usePlanCtx();
  const [v, setV] = useState<Record<string, number | undefined>>(() =>
    Object.fromEntries(family.persons.map((p) => [p.uuid, p.oasResidency?.yearsInCanadaAfter18])),
  );
  const issues = Object.fromEntries(
    family.persons.map((p) => [p.uuid, yearsInCanadaIssue(v[p.uuid], p.dob, today)]),
  );
  const invalid = Object.values(issues).some(Boolean);
  const edits = family.persons
    .filter((p) => v[p.uuid] !== undefined && v[p.uuid] !== p.oasResidency?.yearsInCanadaAfter18)
    .map((p) => fieldEdit(paths.oasYears(p.uuid), Math.round(v[p.uuid]!)));
  return (
    <div className="walk">
      <div className="trap">
        <b>Don&rsquo;t confuse this with CPP.</b> CPP depends on what you contributed from your
        paycheques. OAS depends only on years of residence.
      </div>
      <ol className="steps">
        <li>
          Count the years you&rsquo;ve <b>physically lived in Canada</b> since your 18th birthday.
        </li>
        <li>
          Stop at today: Abi adds the years from now until 65 itself, assuming you stay. If
          you&rsquo;ve lived here since you turned 18, that&rsquo;s your age less 18.
        </li>
        <li>Subtract any stretch you lived abroad for more than a few months.</li>
      </ol>
      <div className="res">
        <div className="two">
          {family.persons.map((p, pi) => (
            <NumberField
              key={p.uuid}
              label={`${name(family, pi)} — years in Canada since 18`}
              value={v[p.uuid]}
              onChange={(n) => setV({ ...v, [p.uuid]: n })}
              placeholder="e.g. 24"
              hint={copyText("fields.yearsInCanadaHint")}
              error={issues[p.uuid]}
              style={{ margin: 0 }}
              testId={`oas-${pi}`}
            />
          ))}
        </div>
        <SaveRow
          disabled={!edits.length || invalid}
          saving={saving}
          onClick={() => void onSave(edits)}
        />
      </div>
    </div>
  );
}

const ITEMS: Record<SharpenId, { t: string; why: string; body: (p: ItemProps) => ReactNode }> = {
  room: {
    t: "How much room is left in your TFSA, RRSP and FHSA",
    why: "Only the CRA knows what you are allowed. Guess low and you leave growth on the table; guess high and you get a penalty.",
    body: (p) => <RoomItem {...p} />,
  },
  work: {
    t: "What you have for retirement through work",
    why: "You told us there is something at work. Until we know which kind, your plan shows less than you will really have.",
    body: (p) => <WorkItem {...p} />,
  },
  job: {
    t: "Your occupation and the year you started each job",
    why: "A defined benefit pension is worked out from your years of service and your salary. Without the start year Abi has to guess your service.",
    body: (p) => <JobItem {...p} />,
  },
  oas: {
    t: "How long you have lived in Canada since you turned 18",
    why: "Old Age Security is based on years of residence, not on what you paid in. We have been assuming a full lifetime.",
    body: (p) => <OasItem {...p} />,
  },
};

const DEBT_RATE = /(?:^mortgages|^loans|\.credit_cards)\[[^\]]+\]\.interest$/;

/** "We estimated X" and "our estimate changed" prompts from GET /api/family/defaults. */
function Estimates({
  ctx,
  onDecide,
  saving,
}: {
  ctx: PlanCtx;
  onDecide: (path: string, d: DefaultDecision_Decision) => void;
  saving: boolean;
}) {
  const defaults = useFamilyDefaults(ctx.doc.version);
  const resolved = useMemo(
    () => (defaults.data?.resolvedFamily ? familyJson(defaults.data.resolvedFamily) : undefined),
    [defaults.data],
  );
  const changes = defaults.data?.defaultChanges ?? [];
  // Only values a person can check at a glance (not payment schedules), with the debt rates the
  // "Fill in missing values" card asks about first, so they're never cut off by the limit.
  const readable = (path: string) => {
    const v = resolved ? valueAt(resolved, path) : undefined;
    return !(v && typeof v === "object" && !Array.isArray(v) && !("year" in v));
  };
  const rank = (path: string) => (DEBT_RATE.test(path) ? 0 : 1);
  const estimated = (defaults.data?.defaultedPaths ?? [])
    .filter(readable)
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, 12);
  if (!changes.length && !estimated.length) return null;
  const nameOf = (uuid: string) => ctx.index.get(uuid)?.name;
  // Whose, or which account, job, or debt: the last listed thing in the path that isn't a person
  // ("Alex’s TFSA — Contributed in total", not two bare "Contributed in total" rows).
  const who = (path: string) => {
    const things = [...path.matchAll(/\[([^\]]+)\]/g)].map((m) => ctx.index.get(m[1]));
    const thing = things.reverse().find((t) => t && t.kindLabel !== "Person");
    if (thing) return `${thing.name} — `;
    const uuid = pathPerson(path);
    const p = ctx.family.persons.find((x) => x.uuid === uuid);
    return p && ctx.family.persons.length > 1 ? `${p.givenName || "You"} — ` : "";
  };
  return (
    <div className="card pad" style={{ marginTop: 16 }} data-testid="estimates">
      {changes.length > 0 && (
        <>
          <h2 style={{ marginBottom: 4 }}>Our estimate changed</h2>
          <p className="mut" style={{ marginBottom: 12 }}>
            You confirmed these earlier. Our estimate has moved since; keep yours or take the new
            one.
          </p>
          {changes.map((c) => (
            <div className="cirow" key={c.path}>
              <div style={{ flex: 1 }}>
                <b>
                  {who(c.path)}
                  {pathLabel(c.path)}
                </b>
                <span className="mut">
                  Yours:{" "}
                  {valueText(
                    c.path,
                    c.confirmedValue ? valueJson(c.confirmedValue) : undefined,
                    nameOf,
                  )}{" "}
                  &middot; now we&rsquo;d estimate{" "}
                  {valueText(
                    c.path,
                    c.currentDefault ? valueJson(c.currentDefault) : undefined,
                    nameOf,
                  )}
                </span>
              </div>
              <div className="ciyn">
                <button
                  type="button"
                  className="ynb"
                  disabled={saving}
                  onClick={() => onDecide(c.path, DefaultDecision_Decision.TAKE_NEW)}
                >
                  Update
                </button>
                <button
                  type="button"
                  className="ynb"
                  disabled={saving}
                  onClick={() => onDecide(c.path, DefaultDecision_Decision.KEEP_MINE)}
                >
                  Keep mine
                </button>
              </div>
            </div>
          ))}
        </>
      )}
      {estimated.length > 0 && (
        <>
          <h2 style={{ margin: changes.length ? "18px 0 4px" : "0 0 4px" }}>What we estimated</h2>
          <p className="mut" style={{ marginBottom: 12 }}>
            We didn&rsquo;t have these, so Abi estimated them. If one is right, say so and
            we&rsquo;ll stop asking; if not, correct it on{" "}
            <Link className="wp-link" to="/app/update-data">
              Update Data
            </Link>
            .
          </p>
          {estimated.map((path) => (
            <div className="cirow" key={path}>
              <div style={{ flex: 1 }}>
                <b>
                  {who(path)}
                  {pathLabel(path)}
                </b>
                <span className="mut">
                  We estimated{" "}
                  {valueText(path, resolved ? valueAt(resolved, path) : undefined, nameOf)}
                </span>
              </div>
              <div className="ciyn">
                <button
                  type="button"
                  className="ynb"
                  disabled={saving}
                  onClick={() => onDecide(path, DefaultDecision_Decision.CONFIRM)}
                >
                  That&rsquo;s right
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

const valueJson = (v: Value): JsonValue => Value.toJson(v);

function Body({ ctx }: { ctx: PlanCtx }) {
  const { family, doc, today } = ctx;
  const save = useFamilySave(doc);
  const done = sharpenDone(family);
  const applies = sharpenApplies(family);
  const acc = planAccuracy(family);
  const ids = (Object.keys(SHARPEN_WEIGHTS) as SharpenId[])
    .filter((id) => applies[id])
    .sort((a, b) => SHARPEN_WEIGHTS[b] - SHARPEN_WEIGHTS[a]);
  const onSave = (edits: FieldEdit[]) => save.run(editsQuestionnaire(edits, today));
  const onDecide = (path: string, decision: DefaultDecision_Decision) =>
    void save.run(
      editsQuestionnaire([], today, [{ path, decision }]),
      "Thanks. Abi is rebuilding your plan now.",
    );

  return (
    <>
      <div
        className="grid2"
        style={{ gridTemplateColumns: "1fr 320px", alignItems: "start", gap: 16 }}
      >
        <div className="card pad">
          <div id="sh-list" data-testid="sharpen-list">
            <DataChecks family={family} plan={ctx.abi} index={ctx.index} />
            {ids.map((id) => (
              <details
                className={`actrow${done[id] ? " done" : ""}`}
                key={id}
                data-testid={`sharpen-${id}`}
              >
                <summary>
                  <span className="ck2" aria-label={done[id] ? "Done" : "Not done yet"} />
                  <span style={{ flex: 1 }}>
                    <b>{ITEMS[id].t}</b>
                    <span className="wy">{ITEMS[id].why}</span>
                  </span>
                  <span className="wt">+{SHARPEN_WEIGHTS[id]}% accuracy</span>
                </summary>
                <div className="act-body">
                  {ITEMS[id].body({ family, onSave, saving: save.pending })}
                </div>
              </details>
            ))}
          </div>
          <SaveFeedback state={save} />
        </div>
        <div className="card pad">
          <div className="eyebrow">Plan accuracy</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "8px 0 10px" }}>
            <div
              id="sh-num"
              style={{ fontSize: 38, fontWeight: 700, letterSpacing: "-.03em", lineHeight: 1 }}
              className="tnum"
              data-testid="sharpen-accuracy"
            >
              {acc.accuracy}%
            </div>
            <span className="mut" id="sh-note">
              {accuracyNote(acc.left)}
            </span>
          </div>
          <div className="acctrack">
            <span id="sh-fill" style={{ width: `${acc.accuracy}%` }} />
          </div>
          <p className="mut" style={{ marginTop: 14 }}>
            Each answer is weighted by how far your plan could move if we had it wrong, not by how
            many are left. Contribution room is worth more than anything else here because it
            decides how much of your money can grow without tax for the next thirty years.
          </p>
          <p className="mut" style={{ marginTop: 10 }}>
            We stop at {ACC_FLOOR}%. No plan is ever exactly right.
          </p>
        </div>
      </div>
      <Estimates ctx={ctx} onDecide={onDecide} saving={save.pending} />
    </>
  );
}

export function SharpenPage() {
  return (
    <PlanPage title="pages.sharpen.title" sub="pages.sharpen.sub" testId="sharpen">
      {(ctx) => (
        <>
          <Link className="backlink" to="/app">
            &#8249; Back to your dashboard
          </Link>
          <div style={{ height: 10 }} />
          <Body ctx={ctx} />
        </>
      )}
    </PlanPage>
  );
}
